import { z } from 'zod'
import { strictify } from '~/utils/strictify'
import { DashboardPayments } from '~/services/api/contracts/requests/DashboardPayments'
import { createRequestOperation } from '~/utils/createRequestOperation'
import { createGeneratedResourceOperation } from '~/services/api/createGeneratedResourceOperation'
import { AppProblemModel } from '@/services/api/AppProblemModel'
import type {
  CheckoutCheckoutRequest,
  DashboardPaymentsQuery,
  OrderPricingPreviewRequest,
  PartnerOrderModificationRequestReviewRequest
} from '~/services/api/generated'
import {
  CheckoutCheckoutSuccessSchema,
  DashboardPaymentsSuccessSchema,
  OrderCancelPathSchema,
  OrderCancelSuccessSchema,
  OrderModificationRequestStorePathSchema,
  OrderModificationRequestStoreSuccessSchema,
  OrderPaymentsPathSchema,
  OrderPaymentsSuccessSchema,
  OrderPricingPreviewPathSchema,
  OrderPricingPreviewSuccessSchema,
  OrderShowPathSchema,
  OrderShowPaymentPathSchema,
  OrderShowPaymentSuccessSchema,
  OrderShowSuccessSchema,
  OrdersAutoDeclinePathSchema,
  OrdersAutoDeclineSuccessSchema,
  OrdersStatusPatchPathSchema,
  OrdersStatusPatchSuccessSchema,
  PartnerOrderModificationRequestReviewSuccessSchema,
  PaymentRetryPaymentPathSchema,
  PaymentRetryPaymentSuccessSchema,
  PaymentStorePaymentPathSchema,
  PaymentStorePaymentSuccessSchema
} from '~/services/api/generated'
import type { OrderStatus } from '~/enums'
import { normalizeCancelOrderResponse } from './normalizeCancelOrderResponse'
import { normalizeAutoDeclineResponse } from './normalizeAutoDeclineResponse'
import { CheckoutResponseParseError } from './CheckoutResponseParseError'
import { CheckoutCheckout } from '~/services/api/contracts/requests/CheckoutCheckout'
import { OrderModificationRequestStore } from '~/services/api/contracts/requests/OrderModificationRequestStore'
import { OrderPricingPreview } from '~/services/api/contracts/requests/OrderPricingPreview'
import { OrdersStatusPatch } from '~/services/api/contracts/requests/OrdersStatusPatch'
import { PartnerOrderModificationRequestReview } from '~/services/api/contracts/requests/PartnerOrderModificationRequestReview'
import { PaymentRetryPayment } from '~/services/api/contracts/requests/PaymentRetryPayment'
import { PaymentStorePayment } from '~/services/api/contracts/requests/PaymentStorePayment'

export function useOrderService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api`

  const client = useApiRequest()

  /**
   * Updates order status (PATCH dashboard/orders/:slug/status).
   */
  const updateStatus = createGeneratedResourceOperation(
    client,
    {
      schema: OrdersStatusPatchPathSchema.strict(),
      parse: (orderSlug: string, _status: OrderStatus) => ({ order: orderSlug }),
      buildUrl: (path) => `${baseURL}/dashboard/orders/${path.order}/status`
    },
    OrdersStatusPatchSuccessSchema,
    'PATCH',
    {
      channel: 'body',
      schema: OrdersStatusPatch({}),
      select: (_orderSlug: string, status: OrderStatus) => ({ status })
    }
  )

  /**
   * Partner approves or rejects a pending modification request
   * (POST dashboard/modification-requests/review).
   */
  async function reviewModificationRequest(body: PartnerOrderModificationRequestReviewRequest) {
    const reason = body.action === 'reject' ? body.rejection_reason?.trim() : undefined
    const reviewSchema = PartnerOrderModificationRequestReview({})
    let requestBody: z.output<typeof reviewSchema>
    try {
      requestBody = reviewSchema.parse({
        order_number: body.order_number,
        modification_request_id: body.modification_request_id,
        action: body.action,
        rejection_reason: reason !== undefined && reason.length > 0 ? reason : undefined
      })
    } catch (error) {
      if (error instanceof z.ZodError) throw AppProblemModel.fromZodError(error)
      throw error
    }

    const response = await client(`${baseURL}/dashboard/modification-requests/review`, {
      method: 'POST',
      body: requestBody
    })
    return strictify(PartnerOrderModificationRequestReviewSuccessSchema).parse(response)
  }

  // A response-parse failure here means the order already committed, so it's wrapped in CheckoutResponseParseError, distinct from a pre-send ZodError.
  async function checkout(body: CheckoutCheckoutRequest) {
    const requestBody = CheckoutCheckout({}).parse(body)
    const response = await client(`${baseURL}/checkout`, {
      method: 'POST',
      body: requestBody
    })

    const parsedResponse = CheckoutCheckoutSuccessSchema.safeParse(response)
    if (parsedResponse.success) return parsedResponse.data
    throw new CheckoutResponseParseError(parsedResponse.error)
  }

  const getOrder = createGeneratedResourceOperation(
    client,
    {
      schema: OrderShowPathSchema,
      parse: (identifier: string) => ({ identifier }),
      buildUrl: (path) => `${baseURL}/orders/${path.identifier}`
    },
    OrderShowSuccessSchema,
    'GET'
  )

  const cancelOrderRequest = createGeneratedResourceOperation(
    client,
    {
      schema: OrderCancelPathSchema,
      parse: (orderNumber: string) => ({ order: orderNumber }),
      buildUrl: (path) => `${baseURL}/orders/${path.order}/cancel`
    },
    OrderCancelSuccessSchema,
    'POST'
  )
  const cancelOrder = async (orderNumber: string) =>
    normalizeCancelOrderResponse(await cancelOrderRequest(orderNumber))

  /**
   * `is_balance_payment` / `balance_due` (APP-1033 balance-retry) are published by
   * `OrderShowPaymentSuccessSchema` directly — no hand-written normalization needed.
   */
  const getPaymentInfo = createGeneratedResourceOperation(
    client,
    {
      schema: OrderShowPaymentPathSchema,
      parse: (identifier: string) => ({ identifier }),
      buildUrl: (path) => `${baseURL}/orders/${path.identifier}/payment`
    },
    OrderShowPaymentSuccessSchema,
    'GET'
  )

  const processPayment = createGeneratedResourceOperation(
    client,
    {
      schema: PaymentStorePaymentPathSchema,
      parse: (identifier: string, _paymentMethodId: number) => ({ identifier }),
      buildUrl: (path) => `${baseURL}/orders/${path.identifier}/payment`
    },
    PaymentStorePaymentSuccessSchema,
    'POST',
    {
      channel: 'body',
      schema: PaymentStorePayment({}),
      select: (_identifier, paymentMethodId) => ({ payment_method_id: paymentMethodId })
    }
  )

  /**
   * Retries the failed automatic remaining-balance charge for a PARTIALLY_PAID
   * order (POST /orders/:identifier/payment-retry). Same response shape as
   * processPayment plus `is_balance_payment` / `balance_due`.
   */
  const retryPayment = createGeneratedResourceOperation(
    client,
    {
      schema: PaymentRetryPaymentPathSchema,
      parse: (identifier: string, _paymentMethodId: number) => ({ identifier }),
      buildUrl: (path) => `${baseURL}/orders/${path.identifier}/payment-retry`
    },
    PaymentRetryPaymentSuccessSchema,
    'POST',
    {
      channel: 'body',
      schema: PaymentRetryPayment({}),
      select: (_identifier, paymentMethodId) => ({ payment_method_id: paymentMethodId })
    }
  )

  /**
   * Submits an order modification request (POST orders/:order_number/modification-requests).
   * Body may include `details`, `items`, or both.
   */
  async function createModificationRequest(orderNumber: string, body: unknown) {
    const path = OrderModificationRequestStorePathSchema.strict().parse({
      order: orderNumber
    })
    const modificationSchema = OrderModificationRequestStore({})
    let requestBody: z.output<typeof modificationSchema>
    try {
      requestBody = modificationSchema.parse(body, { reportInput: true })
    } catch (error) {
      if (error instanceof z.ZodError) throw AppProblemModel.fromZodError(error)
      throw error
    }

    const response = await client(`${baseURL}/orders/${path.order}/modification-requests`, {
      method: 'POST',
      body: requestBody
    })
    return strictify(OrderModificationRequestStoreSuccessSchema).parse(response)
  }

  /**
   * Preview order totals for a hypothetical cart (same pricing as checkout / cart pipeline).
   */
  async function pricingPreview(orderNumber: string, body: unknown) {
    const path = OrderPricingPreviewPathSchema.strict().parse({ order: orderNumber })
    const previewSchema = OrderPricingPreview({})
    let requestBody: OrderPricingPreviewRequest
    try {
      requestBody = previewSchema.parse(body, { reportInput: true })
    } catch (error) {
      if (error instanceof z.ZodError) throw AppProblemModel.fromZodError(error)
      throw error
    }
    return strictify(OrderPricingPreviewSuccessSchema).parse(
      await client(`${baseURL}/orders/${path.order}/pricing-preview`, {
        method: 'POST',
        body: requestBody
      })
    ).data
  }

  const autoDeclineRequest = createGeneratedResourceOperation(
    client,
    {
      schema: OrdersAutoDeclinePathSchema,
      parse: (orderNumber: string) => ({ order: orderNumber }),
      buildUrl: (path) => `${baseURL}/dashboard/orders/${path.order}/auto-decline`
    },
    OrdersAutoDeclineSuccessSchema,
    'POST'
  )
  const autoDecline = async (orderNumber: string) =>
    normalizeAutoDeclineResponse(await autoDeclineRequest(orderNumber))

  const getPayments = createGeneratedResourceOperation(
    client,
    {
      schema: OrderPaymentsPathSchema,
      parse: (identifier: string) => ({ identifier }),
      buildUrl: (path) => `${baseURL}/dashboard/orders/${path.identifier}/payments`
    },
    OrderPaymentsSuccessSchema,
    'GET'
  )

  const getDashboardPayments = createRequestOperation(
    client,
    strictify(DashboardPaymentsSuccessSchema),
    (params?: DashboardPaymentsQuery) => ({
      url: `${baseURL}/dashboard/payments`,
      options: { method: 'GET', params: DashboardPayments({}).parse(params ?? {}) }
    }),
    (response) => response
  )

  return {
    updateStatus,
    autoDecline,
    reviewModificationRequest,
    createModificationRequest,
    pricingPreview,
    checkout,
    cancelOrder,
    getOrder,
    getPaymentInfo,
    processPayment,
    retryPayment,
    getPayments,
    getDashboardPayments
  }
}
